import React from "react";
import { Tooltip } from "@material-ui/core";
import ArrowDropUpIcon from "@material-ui/icons/ArrowDropUp";
import ArrowDropDownIcon from "@material-ui/icons/ArrowDropDown";
import styled from "styled-components";
import { nv, realPointCount } from "./utils";
import InfoTooltip from "../common/InfoTooltip";
import Sparkline from "../common/Sparkline";

const C = {
    surface: "#ffffff",
    border:  "#e2e8f0",
    hover:   "#cbd5e1",
    text:    "#0f172a",
    muted:   "#94a3b8",
    green:   "#15803d",
    red:     "#dc2626",
};

const Tile = styled.div`
    background: ${C.surface};
    border: 1px solid ${C.border};
    border-radius: 12px;
    padding: 14px 16px 10px;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    transition: border-color 120ms ease, box-shadow 120ms ease;
    ${({ $anyClickable }) => $anyClickable && `
        &:hover { border-color: ${C.hover}; box-shadow: 0 1px 2px rgba(15,23,42,0.04); }
    `}
    @media (max-width: 600px) {
        border-radius: 0;
        border-left: none;
        border-right: none;
    }
`;

// Two click zones per tile. Filter zone (title + headline) activates the URL-table filter and
// scrolls to it; Investigate zone (delta + sparkline) deep-links to /trends. Cursor changes
// on each zone are the only affordance — no extra hover styling, to keep the card sober.
const FilterZone = styled.div`
    cursor: ${({ $clickable }) => ($clickable ? "pointer" : "default")};
`;

const InvestigateInline = styled.span`
    cursor: ${({ $clickable }) => ($clickable ? "pointer" : "default")};
    display: inline-flex;
    align-items: center;
`;

const InvestigateBlock = styled.div`
    cursor: ${({ $clickable }) => ($clickable ? "pointer" : "default")};
`;

const Head = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    font-size: 0.62rem;
    font-weight: 700;
    letter-spacing: 1.1px;
    text-transform: uppercase;
    color: ${C.muted};
`;

const NumRow = styled.div`
    display: flex;
    align-items: baseline;
    gap: 8px;
    margin-top: 8px;
`;

const Num = styled.div`
    font-size: 1.75rem;
    font-weight: 700;
    color: ${({ $tone }) => $tone || C.text};
    font-variant-numeric: tabular-nums;
    line-height: 1.05;
    letter-spacing: -0.5px;
`;

const Delta = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 1px;
    font-size: 0.72rem;
    font-weight: 600;
    color: ${({ $tone }) => $tone};
    font-variant-numeric: tabular-nums;
    & svg { font-size: 16px; margin: 0 -3px; }
`;

const SparkBox = styled.div`
    height: 36px;
    margin: 10px -16px 0;
`;

const SparkEmpty = styled.div`
    height: 36px;
    margin: 10px -16px 0;
    border-top: 1px dashed ${C.border};
`;

function DeltaIndicator({ diff, pct, invertDelta }) {
    if (!Number.isFinite(diff) || diff === 0) {
        return <Delta $tone={C.muted}>no change</Delta>;
    }
    const isUp  = diff > 0;
    const isGood = invertDelta ? isUp : !isUp;
    const Icon = isUp ? ArrowDropUpIcon : ArrowDropDownIcon;
    const pctText = Number.isFinite(pct) ? ` ${Math.round(Math.abs(pct))}%` : "";
    return (
        <Delta $tone={isGood ? C.green : C.red}>
            <Icon />
            {nv(Math.abs(diff))}{pctText}
        </Delta>
    );
}

export default function KpiNumberTile({
    title, info, tone, sparkColor = "#2563eb",
    value, unit = "", valueTooltip,
    delta, invertDelta,
    sparkSeries, onFilter, onInvestigate,
}) {
    const displayValue = typeof value === "number" ? nv(value) + unit : String(value ?? "—");
    const sparkData = (sparkSeries || []).map((d) => ({ v: typeof d.value === "number" ? d.value : null }));
    const haveLine = realPointCount(sparkSeries) > 1;

    const handleInvestigate = onInvestigate && ((e) => { e.stopPropagation(); onInvestigate(); });

    return (
        <Tile $anyClickable={!!(onFilter || onInvestigate)}>
            <FilterZone onClick={onFilter} $clickable={!!onFilter}>
                <Head>
                    <span>{title}</span>
                    {info && <InfoTooltip text={info} />}
                </Head>

                <NumRow>
                    {valueTooltip ? (
                        <Tooltip title={valueTooltip}>
                            <Num $tone={tone}>{displayValue}</Num>
                        </Tooltip>
                    ) : (
                        <Num $tone={tone}>{displayValue}</Num>
                    )}
                    <InvestigateInline onClick={handleInvestigate} $clickable={!!onInvestigate}>
                        <DeltaIndicator diff={Number(delta?.diff)} pct={Number(delta?.pct)} invertDelta={invertDelta} />
                    </InvestigateInline>
                </NumRow>
            </FilterZone>

            <InvestigateBlock onClick={onInvestigate} $clickable={!!onInvestigate}>
                {haveLine
                    ? <SparkBox><Sparkline data={sparkData} color={sparkColor} /></SparkBox>
                    : <SparkEmpty />}
            </InvestigateBlock>
        </Tile>
    );
}
